/* global Word */

import { safeAlert, reportResult, paraDetail, RunDetail } from '../runs/common';
import { ensureRunStyles } from '../taskpane/styleEngine';
import { getConfig, LOAConfig } from '../taskpane/configService';

// ═══════════════════════════════════════════════════════════════════════════
// RUN 15: LOA HEAD STYLE — section OOXML approach + Go To navigation
// ═══════════════════════════════════════════════════════════════════════════
// Go To uses paraDetail(uniqueLocalId) — the paragraph approach. After each
// sectionBody.insertOoxml("Replace"), the proxy is invalidated and Word creates
// a fresh paragraph, so we re-find it by text match in the new section body
// and capture its uniqueLocalId for the nav row.
// ═══════════════════════════════════════════════════════════════════════════

function isTocStyle(style: string): boolean {
  const s = (style || "").replace(/\s/g, "").toLowerCase();
  return (
    /^toc\d*$/.test(s)            ||
    s.includes("tableofcontents") ||
    s.includes("contents")
  );
}

function hasRealHyperlinkElement(xml: string): boolean {
  return /<w:hyperlink\s[^>]*(r:id|w:anchor)=/i.test(xml);
}

function applyDefaultParaFontToRuns(paraBlock: string): string {
  let result = paraBlock;

  result = result.replace(
    /(<w:pPr\b[^>]*>)([\s\S]*?)(<\/w:pPr>)/,
    (_pprFull: string, open: string, inner: string, close: string) => {
      if (/<w:rPr\b/.test(inner)) {
        const patchedInner = inner.replace(
          /(<w:rPr\b[^>]*>)([\s\S]*?)(<\/w:rPr>)/,
          (_f: string, rOpen: string, rInner: string, rClose: string) => {
            if (/<w:rStyle\b/.test(rInner)) {
              return rOpen +
                rInner.replace(/<w:rStyle\s+w:val="[^"]*"/, '<w:rStyle w:val="DefaultParagraphFont"') +
                rClose;
            }
            return `${rOpen}<w:rStyle w:val="DefaultParagraphFont"/>${rInner}${rClose}`;
          }
        );
        return open + patchedInner + close;
      }
      return `${open}${inner}<w:rPr><w:rStyle w:val="DefaultParagraphFont"/></w:rPr>${close}`;
    }
  );

  result = result.replace(
    /(<w:r\b[^>]*>)([\s\S]*?)(<\/w:r>)/g,
    (_full: string, rOpen: string, rInner: string, rClose: string) => {
      if (/<w:rPr\b/.test(rInner)) {
        const patchedInner = rInner.replace(
          /(<w:rPr\b[^>]*>)([\s\S]*?)(<\/w:rPr>)/,
          (_f: string, open: string, inner: string, close: string) => {
            if (/<w:rStyle\b/.test(inner)) {
              return open +
                inner.replace(/<w:rStyle\s+w:val="[^"]*"/, '<w:rStyle w:val="DefaultParagraphFont"') +
                close;
            }
            return `${open}<w:rStyle w:val="DefaultParagraphFont"/>${inner}${close}`;
          }
        );
        return rOpen + patchedInner + rClose;
      }
      return `${rOpen}<w:rPr><w:rStyle w:val="DefaultParagraphFont"/></w:rPr>${rInner}${rClose}`;
    }
  );

  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// Per-run mutable state + shared context, threaded through the helpers.
// Bundling context/config/state keeps every helper within the parameter limit.
// ═══════════════════════════════════════════════════════════════════════════

interface Run15State {
  styled: number;
  alreadyCorrect: number;
  skipped: number;
  navCount: number;
  navDetails: RunDetail[];
}

interface Ctx {
  context: Word.RequestContext;
  c: LOAConfig;
  state: Run15State;
}

/** Look-ahead validation: confirm the paragraph after the heading is a real
 *  LOA entry — non-empty, starts with the configured prefix (e.g. "Appendix"),
 *  and contains a genuine hyperlink. Returns true if validation passes. */
async function validateLookAhead(para: Word.Paragraph, ctx: Ctx): Promise<boolean> {
  const { context, c } = ctx;

  const nextPara = para.getNextOrNullObject();
  nextPara.load("text, style");
  await context.sync();

  let lookahead     = nextPara;
  let lookaheadText = "";
  let safetyCounter = 0;

  while (!lookahead.isNullObject && safetyCounter < 10) {
    lookaheadText = (lookahead.text || "").trim();
    if (lookaheadText.length > 0) break;
    const next = lookahead.getNextOrNullObject();
    next.load("text, style");
    await context.sync();
    lookahead = next;
    safetyCounter++;
  }

  if (lookahead.isNullObject || lookaheadText.length === 0) {
    console.log(`    ✗ SKIP — no non-empty next paragraph found`);
    return false;
  }

  console.log(`    look-ahead: "${lookaheadText.substring(0, 70)}"`);

  if (!lookaheadText.startsWith(c.lookaheadPrefix)) {
    console.log(`    ✗ SKIP — next para doesn't start with "${c.lookaheadPrefix}"`);
    return false;
  }

  const nextOoxmlResult = lookahead.getOoxml();
  await context.sync();
  const hasRealLink = hasRealHyperlinkElement(nextOoxmlResult.value);
  console.log(`    hasRealHyperlink: ${hasRealLink}`);

  if (!hasRealLink) {
    console.log(`    ✗ SKIP — no real <w:hyperlink> in next paragraph`);
    return false;
  }
  return true;
}

/** Set explicit fallback properties on a freshly created LoA Head style
 *  (used when the configured base style doesn't exist in the document). */
async function applyExplicitHeadingProps(newStyle: Word.Style, c: LOAConfig): Promise<void> {
  newStyle.font.name                    = "Times New Roman";
  newStyle.font.size                    = 12;
  newStyle.font.bold                    = true;
  newStyle.paragraphFormat.spaceBefore  = 12;
  newStyle.paragraphFormat.spaceAfter   = 6;
  newStyle.paragraphFormat.keepWithNext = true;
  console.log(`    ✓ "${c.headingStyle}" created with explicit properties ("${c.baseStyle}" not found)`);
}

/** Ensure the heading style exists in the document. When created, it inherits
 *  from c.baseStyle if that style is present, else gets explicit properties. */
async function ensureHeadingStyle(ctx: Ctx): Promise<void> {
  const { context, c } = ctx;

  const loaStyle = context.document.getStyles().getByNameOrNullObject(c.headingStyle);
  loaStyle.load("isNullObject");
  await context.sync();

  if (!loaStyle.isNullObject) {
    console.log(`    ✓ "${c.headingStyle}" style already exists`);
    return;
  }

  console.log(`    Creating "${c.headingStyle}" style...`);
  const newStyle = context.document.addStyle(c.headingStyle, Word.StyleType.paragraph);
  await context.sync();

  // LoA Head is based on c.baseStyle — inherit its properties when available.
  const baseStyle = context.document.getStyles().getByNameOrNullObject(c.baseStyle);
  baseStyle.load("isNullObject");
  await context.sync();

  if (baseStyle.isNullObject) {
    await applyExplicitHeadingProps(newStyle, c);
  } else {
    newStyle.baseStyle = c.baseStyle;
    console.log(`    ✓ "${c.headingStyle}" based on "${c.baseStyle}"`);
  }

  await context.sync();
  console.log(`    ✓ "${c.headingStyle}" style created`);
}

interface ParaBlockLocation {
  pOpenIdx: number;
  pCloseIdx: number;
  paraBlock: string;
}

/** Locate the enclosing <w:p>...</w:p> block for the heading text within the
 *  section OOXML. Returns null (after logging) if it can't be pinpointed. */
function locateParagraphBlock(sectionOoxml: string, actualText: string): ParaBlockLocation | null {
  let targetPos = sectionOoxml.indexOf(`>${actualText}</w:t>`);
  if (targetPos === -1) targetPos = sectionOoxml.indexOf(`>${actualText}<`);
  if (targetPos === -1) {
    const lowerOoxml  = sectionOoxml.toLowerCase();
    const lowerNeedle = `>${actualText.toLowerCase()}<`;
    const lowerPos    = lowerOoxml.indexOf(lowerNeedle);
    if (lowerPos !== -1) {
      targetPos = lowerPos;
      console.log(`    (matched via lowercase fallback at pos ${targetPos})`);
    }
  }
  if (targetPos === -1) {
    console.log(`    ⚠ Text "${actualText}" not found in section OOXML — skipping`);
    return null;
  }

  const beforeText = sectionOoxml.substring(0, targetPos);
  const pOpenIdx   = beforeText.lastIndexOf("<w:p ");
  if (pOpenIdx === -1) {
    console.log(`    ⚠ Could not find enclosing <w:p> — skipping`);
    return null;
  }

  const afterTarget  = sectionOoxml.substring(targetPos);
  const pCloseOffset = afterTarget.indexOf("</w:p>");
  if (pCloseOffset === -1) {
    console.log(`    ⚠ Could not find closing </w:p> — skipping`);
    return null;
  }

  const pCloseIdx = targetPos + pCloseOffset + "</w:p>".length;
  const paraBlock = sectionOoxml.substring(pOpenIdx, pCloseIdx);
  console.log(`    Paragraph block length: ${paraBlock.length}`);

  return { pOpenIdx, pCloseIdx, paraBlock };
}

/** Patch the <w:p> block: set the heading pStyle (unless already correct) and
 *  apply DefaultParagraphFont to all runs. */
function patchParagraphBlock(
  paraBlock: string,
  xmlStyleCorrect: boolean,
  pStyleMatch: RegExpExecArray | null,
  headingStyleXml: string
): string {
  let patchedBlock: string = paraBlock;

  if (!xmlStyleCorrect) {
    if (pStyleMatch) {
      patchedBlock = patchedBlock.replace(
        /<w:pStyle\s+w:val="[^"]+"/,
        `<w:pStyle w:val="${headingStyleXml}"`
      );
    } else if (/<w:pPr\b[^>]*>/.test(patchedBlock)) {
      patchedBlock = patchedBlock.replace(
        /(<w:pPr\b[^>]*>)/,
        `$1<w:pStyle w:val="${headingStyleXml}"/>`
      );
    } else {
      patchedBlock = patchedBlock.replace(
        /(<w:p\b[^>]*>)/,
        `$1<w:pPr><w:pStyle w:val="${headingStyleXml}"/></w:pPr>`
      );
    }
  }

  patchedBlock = applyDefaultParaFontToRuns(patchedBlock);
  console.log(`    DefaultParagraphFont applied to runs in OOXML`);

  return patchedBlock;
}

/** Ensure the heading style DEFINITION exists in the OOXML package, cloning it
 *  from the paragraph's previous style definition when necessary. */
function ensureStyleDefinitionInPackage(
  finalOoxml: string,
  currentXmlStyle: string,
  c: LOAConfig
): string {
  if (finalOoxml.includes(`w:styleId="${c.headingStyleXml}"`)) {
    console.log(`    ${c.headingStyleXml} style definition already in package`);
    return finalOoxml;
  }

  if (currentXmlStyle === "none") return finalOoxml;

  const oldStyleRegex = new RegExp(
    String.raw`<w:style\s[^>]*w:styleId="${currentXmlStyle}"[\s\S]*?<\/w:style>`
  );
  const oldStyleMatch = oldStyleRegex.exec(finalOoxml);
  if (oldStyleMatch) {
    const newStyleDef = oldStyleMatch[0]
      .replace(`w:styleId="${currentXmlStyle}"`, `w:styleId="${c.headingStyleXml}"`)
      .replace(/<w:name\s+w:val="[^"]*"/, `<w:name w:val="${c.headingStyle}"`);
    console.log(`    Cloned "${currentXmlStyle}" → added ${c.headingStyleXml} style definition`);
    return finalOoxml.replace(oldStyleMatch[0], oldStyleMatch[0] + newStyleDef);
  }

  console.log(`    ⚠ Could not find "${currentXmlStyle}" style definition to clone`);
  return finalOoxml;
}

const normaliseForMatch = (s: string): string => (s || "").trim().toUpperCase();

/** Re-locate the just-styled paragraph (its old proxy was invalidated by
 *  insertOoxml("Replace")) within the fresh section body and push a Go To nav
 *  row with its new uniqueLocalId. */
async function captureGoToNavRow(
  sectionBody: Word.Body,
  actualText: string,
  noteText: string,
  ctx: Ctx
): Promise<void> {
  const { context, state } = ctx;

  try {
    const freshSectionParas = sectionBody.paragraphs;
    freshSectionParas.load("items/text, items/uniqueLocalId");
    await context.sync();

    const needle = normaliseForMatch(actualText);
    let matched: Word.Paragraph | null = null;
    for (const sp of freshSectionParas.items) {
      if (normaliseForMatch(sp.text || "") === needle) {
        matched = sp;
        break;
      }
    }

    if (matched) {
      state.navDetails.push(paraDetail(matched.uniqueLocalId, actualText, noteText));
      state.navCount++;
      console.log(`    Go To nav row added (paraId=${matched.uniqueLocalId})`);
    } else {
      console.log(`    ⚠ Could not re-locate paragraph for Go To — skipping nav row`);
    }
  } catch (navErr) {
    console.log(`    ⚠ Nav row capture failed: ${(navErr as Error).message}`);
  }
}

/** Apply the LoA-head style to one confirmed heading paragraph: locate its
 *  OOXML block, patch style + font, ensure the style definition is present,
 *  write the modified section OOXML back, and capture the Go To nav row. */
async function applyHeadingViaOoxml(
  sectionBody: Word.Body,
  para: Word.Paragraph,
  sectionIndex: number,
  ctx: Ctx
): Promise<void> {
  const { context, c, state } = ctx;

  await ensureHeadingStyle(ctx);

  // ── Get the entire section body OOXML ──
  console.log(`    → Getting section ${sectionIndex + 1} body OOXML...`);
  const sectionOoxmlResult = sectionBody.getOoxml();
  await context.sync();

  const sectionOoxml = sectionOoxmlResult.value;
  console.log(`    Section OOXML length: ${sectionOoxml.length}`);

  const actualText = para.text.trim();
  const loc = locateParagraphBlock(sectionOoxml, actualText);
  if (!loc) {
    state.skipped++;
    return;
  }

  // ── Check current XML pStyle ──
  const pStyleRe        = /<w:pStyle\s+w:val="([^"]+)"/;
  const pStyleMatch     = pStyleRe.exec(loc.paraBlock);
  const currentXmlStyle = pStyleMatch ? pStyleMatch[1] : "none";
  console.log(`    Current XML pStyle: "${currentXmlStyle}"`);

  // ── Check if XML style already correct ──
  const xmlStyleCorrect = currentXmlStyle === c.headingStyleXml;
  if (xmlStyleCorrect) {
    console.log(`    → Already ${c.headingStyleXml} in XML — will still apply DefaultParagraphFont`);
    state.alreadyCorrect++;
  }

  const patchedBlock = patchParagraphBlock(loc.paraBlock, xmlStyleCorrect, pStyleMatch, c.headingStyleXml);

  const modifiedOoxml =
    sectionOoxml.substring(0, loc.pOpenIdx) +
    patchedBlock +
    sectionOoxml.substring(loc.pCloseIdx);

  console.log(`    Modified OOXML length: ${modifiedOoxml.length} (delta: ${modifiedOoxml.length - sectionOoxml.length})`);

  const finalOoxml = ensureStyleDefinitionInPackage(modifiedOoxml, currentXmlStyle, c);

  sectionBody.insertOoxml(finalOoxml, "Replace");
  await context.sync();

  // ── Re-locate the just-styled paragraph and capture its uniqueLocalId. ──
  const noteText = xmlStyleCorrect ? "DefaultParagraphFont applied" : `→ ${c.headingStyle}`;
  await captureGoToNavRow(sectionBody, actualText, noteText, ctx);

  if (!xmlStyleCorrect) state.styled++;
  const doneMsg = xmlStyleCorrect
    ? `DefaultParagraphFont applied to existing ${c.headingStyleXml}`
    : `Styled ${c.headingStyle} + DefaultParagraphFont`;
  console.log(`    → ${doneMsg} via section OOXML ✓`);
}

/** Process a single search hit: exact-match gate, TOC-style gate, look-ahead
 *  validation, then apply the heading style. */
async function processSearchResult(
  sectionBody: Word.Body,
  result: Word.Range,
  resultIndex: number,
  sectionIndex: number,
  phrase: string,
  ctx: Ctx
): Promise<void> {
  const { context, c, state } = ctx;

  const para = result.paragraphs.getFirst();
  para.load("text, style");
  await context.sync();

  // ── Exact paragraph match check ──
  const paraText = (para.text || "").trim().toUpperCase();
  if (paraText !== phrase.toUpperCase()) {
    console.log(`    [${resultIndex}] Skipped — not exact match: "${para.text.trim().substring(0, 50)}..."`);
    state.skipped++;
    return;
  }

  const currentStyle = para.style || "";
  console.log(`    [${resultIndex}] "${para.text.trim()}" | Style: "${currentStyle}"`);

  // ── Skip TOC entry paragraphs ──
  if (isTocStyle(currentStyle)) {
    console.log(`    [${resultIndex}] Skipped — TOC entry (style: "${currentStyle}"), not the real heading`);
    state.skipped++;
    return;
  }

  // ── Check if style already correct (API level) ──
  const styleNorm       = currentStyle.replace(/\s/g, "").toLowerCase();
  const apiStyleCorrect = styleNorm === c.headingStyleXml.toLowerCase();
  if (apiStyleCorrect) {
    console.log(`    → Already "${c.headingStyle}" (API) — will still apply DefaultParagraphFont`);
  }

  // ── Look-ahead validation ──
  const lookAheadOk = await validateLookAhead(para, ctx);
  if (!lookAheadOk) {
    state.skipped++;
    return;
  }

  await applyHeadingViaOoxml(sectionBody, para, sectionIndex, ctx);
}

/** Process one phrase within one section: run the search and feed each hit
 *  through processSearchResult. */
async function processPhraseInSection(
  sectionBody: Word.Body,
  phrase: string,
  sectionIndex: number,
  ctx: Ctx
): Promise<void> {
  const { context } = ctx;

  const results = sectionBody.search(phrase, {
    matchCase: false,
    matchWholeWord: false,
    matchWildcards: false,
  });
  results.load("items");
  await context.sync();

  if (results.items.length === 0) return;

  console.log(`\n  Section ${sectionIndex + 1}: Found "${phrase}" (${results.items.length} hit(s))`);

  for (let r = 0; r < results.items.length; r++) {
    await processSearchResult(sectionBody, results.items[r], r, sectionIndex, phrase, ctx);
  }
}

function reportRun15Results(ctx: Ctx): void {
  const { c, state } = ctx;

  console.log("\n" + "═".repeat(70));
  console.log(`Styled: ${state.styled} | Already correct (DefaultParagraphFont applied): ${state.alreadyCorrect} | Skipped: ${state.skipped}`);
  console.log(`Go To nav rows added: ${state.navCount}`);
  console.log("✓ List of Appendices check complete!");
  console.log("═".repeat(70));

  if (state.styled > 0 || state.alreadyCorrect > 0) {
    reportResult('List of Appendices Styling Complete', [
      { label: `${c.headingStyle} entries styled`, count: state.styled },
      { label: 'Already correct (DefaultParagraphFont applied)', count: state.alreadyCorrect },
      { label: 'Skipped', count: state.skipped },
      ...state.navDetails,
    ]);
  } else {
    // No LOA heading found (or all matches skipped). Still emit a summary
    // card so the user sees a zero-change entry in the QC Summary panel —
    // count-only rows (no paraId / bookmarkId) render without a Go To button.
    const phraseList = c.searchPhrases.map(p => `"${p}"`).join(" or ");
    reportResult('List of Appendices — no changes needed', [
      { label: `No ${phraseList} paragraphs found`, note: 'No changes made' },
      { label: `${c.headingStyle} entries styled`, count: 0 },
      { label: 'Skipped', count: state.skipped },
    ]);
  }
}

export async function run15(): Promise<"Success" | ["Failure", string]> {
  // ── Read config ──
  const cfg = getConfig();
  const c: LOAConfig = cfg.run15_LOA;

  try {
    await Word.run(async (context) => {
      await ensureRunStyles(context, 15);
      console.log("═".repeat(70));
      console.log("RUN 15: List of Appendices — LoA Head Styling (Section OOXML + Go To)");
      console.log("═".repeat(70));

      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      const totalSections = sections.items.length;
      console.log(`Total sections: ${totalSections}`);

      const state: Run15State = {
        styled: 0,
        alreadyCorrect: 0,
        skipped: 0,
        navCount: 0,
        navDetails: [],
      };
      const ctx: Ctx = { context, c, state };

      for (let s = 0; s < totalSections; s++) {
        const sectionBody = sections.items[s].body;

        for (const phrase of c.searchPhrases) {
          await processPhraseInSection(sectionBody, phrase, s, ctx);
        }
      }

      await context.sync();

      reportRun15Results(ctx);
    });

    return "Success";

  } catch (error) {
    console.error("Error in run15:", error);
    console.error("Message:", (error as Error).message);
    safeAlert("An error occurred during List of Appendices check.\nCheck console (F12) for details.");
    return ["Failure", (error as Error).message];
  }
}